import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import type { Player } from '../../../sdk/model';
import { toSalary } from '../../util/SalaryDisplay';
import LeagueRosterCapSummary from './LeagueRosterCapSummary';

function LeagueRosterCapPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);

  const user = useAppSelector((state) => state.user);
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad);

  const league = useLoadLeagueData({
    leagueId,
    userId: user.id,
    loadBasicDraftData: false,
    loadFullDraftData: false,
    loadContractData: true,
    loadBasicAuctionData: false,
    loadFullAuctionData: false,
    loadRosterData: true,
    loadRosterFor: [],
    loadWaivedContracts: false,
    loadScheduleData: false,
    isException,
    setIsException,
    isLoadComplete,
    setLoadComplete,
  });

  const playerMap = React.useMemo(
    () =>
      new Map<string | undefined, Player | undefined>(
        playerDataLoad.flatMap((nflTeam) => nflTeam.players).map((player) => [player.id, player])
      ),
    [playerDataLoad]
  );

  if (!league) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Roster &amp; Cap Space
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Remaining salary cap and open roster spots for each team
        {league.currentSeason?.nflSeason?.salaryCap !== undefined
          ? ` (${toSalary(league.currentSeason.nflSeason.salaryCap)} league cap)`
          : ''}
        . Position chips count players by position across the whole roster (starters, flex, and
        bench). Red = none; dark green = exact requirement; green = one above required; light
        green = above depth threshold (QB &gt;2, RB &gt;3, WR &gt;4, TE &gt;2).
      </Typography>
      <LeagueRosterCapSummary
        league={league}
        userId={user.id}
        playerMap={playerMap}
        showIntro={false}
      />
    </Container>
  );
}

export default LeagueRosterCapPage;
